"use client";

import BookForMeForm from "./component/BookForMeForm";
import BookForMeSuccessView from "./component/BookForMeSuccessView";
import BookForMeResultsView from "./component/BookForMeResultsView";
import { memo, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryStates } from "nuqs";
import { useCreateBookForMe } from "@/hooks/book-for-me/useBookForMe";
import { toast } from "@/hooks/useToast";
import { Card } from "@/components/ui/card";
import { Typography } from "@/components/Typography";
import {
  BookForMeHelpBanner,
  renderStepIcon,
} from "./component/BookForMeComponents";
import {
  BookForMeFormData,
  bookForMeFormSchema,
  bookForMeQueryParsers,
  buildBookForMePayload,
  DEFAULT_BOOK_FOR_ME_FORM_VALUES,
  MOCK_HOW_IT_WORKS_STEPS,
} from "./book-for-me.type";

const BookForMeDataPage = () => {
  const [query, setQuery] = useQueryStates(bookForMeQueryParsers, {
    history: "push",
  });

  const step = query.step;

  const submittedData = useMemo<BookForMeFormData | null>(() => {
    if (step === "form") return null;
    return {
      destination: query.destination ?? "",
      destinationId: query.destinationId ?? "",
      date: query.date ?? "",
      guests: query.guests ?? DEFAULT_BOOK_FOR_ME_FORM_VALUES.guests,
      budgetMin: query.budgetMin ?? "",
      budgetMax: query.budgetMax ?? "",
      preferences: query.preferences ?? [],
      additionalRequests: query.additionalRequests ?? "",
    };
  }, [query, step]);

  // Manage form state using react-hook-form with zod validation
  const form = useForm<BookForMeFormData>({
    resolver: zodResolver(bookForMeFormSchema),
    defaultValues: DEFAULT_BOOK_FOR_ME_FORM_VALUES,
    mode: "onSubmit",
  });

  const { mutate, isPending: isSubmitting } = useCreateBookForMe();

  const handleFormSubmit = useCallback(
    (data: BookForMeFormData) => {
      mutate(buildBookForMePayload(data), {
        onSuccess: () => {
          void setQuery({
            step: "finding",
            destination: data.destination || null,
            destinationId: data.destinationId || null,
            date: data.date || null,
            guests: data.guests || null,
            budgetMin: data.budgetMin || null,
            budgetMax: data.budgetMax || null,
            preferences: data.preferences.length > 0 ? data.preferences : null,
            additionalRequests: data.additionalRequests || null,
          });
        },
        onError: (error: Error) => {
          toast({
            variant: "error",
            title: "Failed to send enquiry",
            description: error.message,
          });
        },
      });
    },
    [mutate, setQuery],
  );

  if (step === "finding" && submittedData) {
    return (
      <BookForMeSuccessView
        data={submittedData}
        onBackToHome={() => {
          form.reset(DEFAULT_BOOK_FOR_ME_FORM_VALUES);
          void setQuery({
            step: "form",
            destination: null,
            destinationId: null,
            date: null,
            guests: null,
            budgetMin: null,
            budgetMax: null,
            preferences: null,
            additionalRequests: null,
          });
        }}
        onViewMyRequest={() => {
          void setQuery({ step: "results" });
        }}
      />
    );
  }

  if (step === "results" && submittedData) {
    return <BookForMeResultsView data={submittedData} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col-reverse justify-between gap-4 lg:flex-row">
        {/* Left Column: Form & Help Banner */}
        <section className="w-full space-y-4 lg:w-[65%]">
          <BookForMeForm
            form={form}
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
          />
          <BookForMeHelpBanner />
        </section>

        {/* Right Column: How Book for Me works Sticky Card */}
        <section className="w-full lg:sticky lg:top-40 lg:w-[35%] self-start">
          <Card className="space-y-6 rounded-3xl border bg-white p-6 shadow-product-card border-none">
            <Typography
              variant="subtitle1"
              mdVariant="h6"
              className="font-semibold text-heading"
            >
              How Book for Me works
            </Typography>

            <div className="space-y-5">
              {MOCK_HOW_IT_WORKS_STEPS.map((item) => (
                <div key={item.step} className="flex items-start gap-3.5">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#EBFBFF]">
                    {renderStepIcon(item.iconName)}
                  </div>

                  <div className="space-y-0.5">
                    <Typography
                      variant="button-sm"
                      className="font-semibold text-heading"
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className="text-xs leading-relaxed text-body md:text-xs"
                    >
                      {item.description}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </section>
      </div>
    </div>
  );
};

export const MemoBookForMeDataPage = memo(BookForMeDataPage);
export default MemoBookForMeDataPage;
